/**
 * Detecção e configuração de marca (multi-brand: eMiner × Máquina de Leads).
 *
 * Hosts de PRODUÇÃO (deploy atual):
 *  - my.eminer.app               → eMiner        (extId mjbjdgajnigneaoankmolkojdbbnmjpm)
 *  - minha.maquinadeleads.com    → Lead Machine  (extId akobgmenldnlbglcoaekkiolkjkfhmam)
 *  - dashboard.eminer.app / app.maquinadeleads.com → hosts antigos (mantidos p/ transição)
 *  - *.eminer.app (admin/sandbox)→ marca vinda do cookie "extension"
 *
 * REGRA (§7.7): nunca hardcodar marca em componentes; sempre via getBrand()/useBrand().
 */
import { API_BASE_FORCE_DEV, API_BASE_OVERRIDE, API_BASE_PROD, API_BASE_SANDBOX, FORCE_BRAND } from './env';
import { getCookie } from '../lib/cookies';

export type BrandSource = 'eminer' | 'lead-machine';

export interface Brand {
  source: BrandSource;
  extensionId: string;
  apiBase: string;
  appTitle: string;
  defaultLang: 'pt' | 'en' | 'es' | 'fr';
  isSandbox: boolean;
  /** URL do checkout por marca (legado: `checkout.eminer.app` / `checkout.maquinadeleads.com`). */
  checkoutUrl: string;
  /** Link da extensão na Chrome Web Store (para orientar a instalação). */
  webStoreUrl: string;
}

const EXTENSION_IDS: Record<BrandSource, string> = {
  eminer: 'mjbjdgajnigneaoankmolkojdbbnmjpm',
  'lead-machine': 'akobgmenldnlbglcoaekkiolkjkfhmam',
};

function hostname(): string {
  try {
    return window.location.hostname.toLowerCase();
  } catch {
    return '';
  }
}

/** Hosts de produção por marca (o novo primeiro; o antigo mantido p/ transição de deploy). */
const EMINER_PROD_HOSTS = ['my.eminer.app', 'dashboard.eminer.app'];
const LEADMACHINE_PROD_HOSTS = ['minha.maquinadeleads.com', 'app.maquinadeleads.com'];

function resolveSource(host: string): { source: BrandSource; isSandbox: boolean } {
  // Override explícito de desenvolvimento.
  if (FORCE_BRAND === 'eminer' || FORCE_BRAND === 'lead-machine') {
    return { source: FORCE_BRAND, isSandbox: true };
  }
  // Produção eMiner (my.eminer.app). Vem ANTES do ramo sandbox `*.eminer.app`.
  if (EMINER_PROD_HOSTS.includes(host)) {
    return { source: 'eminer', isSandbox: false };
  }
  // Produção Máquina de Leads (minha.maquinadeleads.com + demais subdomínios da marca).
  if (LEADMACHINE_PROD_HOSTS.includes(host) || host.includes('maquinadeleads.com')) {
    return { source: 'lead-machine', isSandbox: false };
  }
  // Prévias dos branches no Workers Builds (<branch>-leadmachine-frontend.<conta>.workers.dev):
  // a extensão não grava o cookie da marca nesse domínio, então vale a Máquina de Leads; o
  // cookie "extension=eminer" ainda troca para o eMiner, para testar a outra marca.
  if (host.endsWith('.workers.dev') && host.includes('leadmachine-frontend')) {
    return {
      source: getCookie('extension') === 'eminer' ? 'eminer' : 'lead-machine',
      isSandbox: true,
    };
  }
  // Sandbox/admin em *.eminer.app e fallback local (localhost):
  // a marca vem do cookie "extension" gravado pela extensão (igual ao legado).
  const cookieSource = getCookie('extension');
  if (cookieSource === 'lead-machine') return { source: 'lead-machine', isSandbox: true };
  return { source: 'eminer', isSandbox: true };
}

function resolveApiBase(isSandbox: boolean): string {
  // VITE_API_BASE (tooling local de dev/teste) tem precedência máxima.
  if (API_BASE_OVERRIDE) return ensureTrailingSlash(API_BASE_OVERRIDE);
  // PROVISÓRIO: força a API de desenvolvimento (admin) em todo o painel. Ver env.ts.
  if (API_BASE_FORCE_DEV) return ensureTrailingSlash(API_BASE_FORCE_DEV);
  return isSandbox ? API_BASE_SANDBOX : API_BASE_PROD;
}

function ensureTrailingSlash(u: string): string {
  return u.endsWith('/') ? u : u + '/';
}

let cached: Brand | null = null;

export function getBrand(): Brand {
  if (cached) return cached;
  const host = hostname();
  const { source, isSandbox } = resolveSource(host);
  cached = {
    source,
    extensionId: EXTENSION_IDS[source],
    apiBase: resolveApiBase(isSandbox),
    appTitle: source === 'lead-machine' ? 'Máquina de Leads' : 'eMiner',
    defaultLang: source === 'lead-machine' ? 'pt' : 'en',
    isSandbox,
    checkoutUrl: source === 'lead-machine' ? 'https://checkout.maquinadeleads.com' : 'https://checkout.eminer.app',
    webStoreUrl: `https://chromewebstore.google.com/detail/${EXTENSION_IDS[source]}`,
  };
  return cached;
}

/** Somente para testes: permite reavaliar a marca. */
export function __resetBrandCache(): void {
  cached = null;
}
